import { HierarcicalObjectReference } from "@novorender/data-js-api";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { Tree } from "components/objectTree/types";
import { buildFilteredTree } from "components/window/core/objectTree/utils";
import { ModelTreeNodeData } from "features/modelTree/types";
import { emptyModelTree } from "features/modelTree/utils";
import { telemetry } from "telemetry";
import { AppObjectDB } from "utils/appObjectDb";

const MINIMUM_SEARCH_TERM_LENGTH = 2;
const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

export type UseObjectTreeSearchReturn = {
    searchTerm: string;
    filteredObjectIds: Set<number> | null;
    tree: Tree<ModelTreeNodeData>;
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
    treeKey: number;
    isSearching: boolean;
    hasNoResults: boolean;
    searchError: string | null;
    handleSearchChange: (value: string) => void;
    handleSearchSubmit: () => Promise<void>;
};

/**
 * Manages object tree search state and behavior.
 *
 * Used by both ObjectTreeWindow and ObjectsPanel to provide consistent
 * search UX across all object tree instances.
 *
 * Search is debounced: typing triggers a search automatically after a short pause
 * (`toleranceMs`, which defaults to {@link DEFAULT_SEARCH_DEBOUNCE_MS}), so users get
 * live filtering without a separate Enter press.
 * Pressing Enter / clicking the search icon fires immediately (cancelling any pending
 * debounce), matching the behaviour of the Properties panel search.
 *
 * All in-flight db.search iterators are aborted via AbortController when a new search
 * starts, when the input is cleared, or when the component unmounts.
 *
 * @param toleranceMs - How long to wait after the last keystroke before firing the
 *   remote search. Defaults to {@link DEFAULT_SEARCH_DEBOUNCE_MS}. Increase for slower
 *   connections or decrease for snappier local indices.
 */
export function useObjectTreeSearch(
    db: AppObjectDB | undefined,
    mainObject: number | undefined,
    toleranceMs: number = DEFAULT_SEARCH_DEBOUNCE_MS,
): UseObjectTreeSearchReturn {
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState("");
    const [filteredObjectIds, setFilteredObjectIds] = useState<Set<number> | null>(null);
    const [tree, setTree] = useState<Tree<ModelTreeNodeData>>(emptyModelTree);
    const [treeKey, setTreeKey] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [hasNoResults, setHasNoResults] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    const searchAbortController = useRef<AbortController>(new AbortController());
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Always-fresh ref so debounce closure calls the latest performSearch even
    // if the callback identity changed (e.g. db became available after mount).
    const performSearchRef = useRef<(term: string) => Promise<boolean>>(async () => false);

    const abortSearch = useCallback(() => {
        searchAbortController.current.abort();
        searchAbortController.current = new AbortController();
    }, []);

    const clearDebounce = useCallback(() => {
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    const clearSearchState = useCallback(() => {
        clearDebounce();
        abortSearch();
        setIsSearching(false);
        setFilteredObjectIds(null);
        setTree(emptyModelTree);
        setTreeKey((prev) => prev + 1);
        setHasNoResults(false);
        setSearchError(null);
    }, [abortSearch, clearDebounce]);

    const searchDatabase = useCallback(
        async (term: string, abortSignal: AbortSignal): Promise<HierarcicalObjectReference[]> => {
            if (!db) {
                return [];
            }

            const iterator = db.search({ searchPattern: [{ property: "name", value: term }] }, abortSignal);

            const matchingObjects: HierarcicalObjectReference[] = [];
            for await (const obj of iterator) {
                if (abortSignal.aborted) {
                    break;
                }
                matchingObjects.push(obj);
            }
            return matchingObjects;
        },
        [db],
    );

    const updateSearchState = useCallback((matchingObjects: HierarcicalObjectReference[]) => {
        if (matchingObjects.length === 0) {
            setFilteredObjectIds(null);
            setTree(emptyModelTree);
            setHasNoResults(true);
            return;
        }

        const filteredTree = buildFilteredTree(matchingObjects);
        const allFilteredIds = new Set(matchingObjects.map((obj) => obj.id));
        setFilteredObjectIds(allFilteredIds);
        setTree(filteredTree);
        setHasNoResults(false);
    }, []);

    const performSearch = useCallback(
        async (term: string): Promise<boolean> => {
            if (!db) {
                return false;
            }

            if (!term || term.length < MINIMUM_SEARCH_TERM_LENGTH) {
                return false;
            }

            abortSearch();
            // Capture the controller/signal for *this* search so that a later
            // call to abortSearch() (which replaces searchAbortController.current)
            // does not cause stale searches to mis-identify themselves as current.
            const localController = searchAbortController.current;
            const localSignal = localController.signal;

            setIsSearching(true);
            setHasNoResults(false);
            setSearchError(null);

            try {
                const matchingObjects = await searchDatabase(term, localSignal);

                // Only update state if this search hasn't been superseded.
                if (!localSignal.aborted) {
                    updateSearchState(matchingObjects);
                    return true;
                }
            } catch (error) {
                if (!localSignal.aborted) {
                    console.error("Search error:", error);
                    telemetry.trackCaughtException({
                        source: "useObjectTreeSearch.performSearch",
                        category: "data",
                        error,
                        severity: "error",
                    });
                    setSearchError(t("errorLoadingSearchResults"));
                    setFilteredObjectIds(null);
                    setTree(emptyModelTree);
                    setHasNoResults(false);
                }
            } finally {
                // Only clear the loading indicator if this search is still current.
                if (!localSignal.aborted) {
                    setIsSearching(false);
                }
            }

            // Search was aborted or superseded — results were not applied.
            return false;
        },
        [db, abortSearch, searchDatabase, updateSearchState, t],
    );

    // Keep the ref in sync so the debounce closure always uses the latest version.
    performSearchRef.current = performSearch;

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchTerm(value);
            clearDebounce();

            // Abort any in-flight search immediately so its results are discarded
            // and network/CPU resources are freed.
            abortSearch();

            // Normalize for length check and search — trim so that debounced
            // searches are consistent with Enter/submit (which also trims).
            const trimmed = value.trim();

            if (trimmed.length === 0) {
                clearSearchState();
                return;
            }

            // Don't schedule a search for values shorter than the minimum.
            // If a previous search produced results, errored, or is still
            // in-flight, clear/abort immediately so the UI never shows stale
            // results that don't match the current (too-short) term.
            if (trimmed.length < MINIMUM_SEARCH_TERM_LENGTH) {
                if (filteredObjectIds !== null || hasNoResults || searchError !== null || isSearching) {
                    clearSearchState();
                }
                return;
            }

            debounceTimerRef.current = setTimeout(() => {
                debounceTimerRef.current = null;
                void performSearchRef.current(trimmed);
            }, toleranceMs);
        },
        [
            clearDebounce,
            abortSearch,
            clearSearchState,
            toleranceMs,
            filteredObjectIds,
            hasNoResults,
            searchError,
            isSearching,
        ],
    );

    // Pressing Enter or clicking the search icon fires immediately without waiting
    // for the debounce, cancelling any pending debounce first.
    const handleSearchSubmit = useCallback(async () => {
        clearDebounce();
        const term = searchTerm.trim();

        if (term.length < MINIMUM_SEARCH_TERM_LENGTH) {
            clearSearchState();
            return;
        }

        const searched = await performSearch(term);
        if (searched) {
            void track(Events.objectTree.searched, { search_term: term });
        }
    }, [clearDebounce, clearSearchState, performSearch, searchTerm]);

    // Reset search when main object is changed and that object isn't in the filtered set.
    useEffect(() => {
        if (mainObject !== undefined && filteredObjectIds && !filteredObjectIds.has(mainObject)) {
            setSearchTerm("");
            clearSearchState();
        }
    }, [mainObject, filteredObjectIds, clearSearchState]);

    // Cleanup on unmount: cancel debounce and abort any in-flight search.
    useEffect(() => {
        return () => {
            clearDebounce();
            searchAbortController.current.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        handleSearchChange,
        handleSearchSubmit,
    };
}
